import React from 'react';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import { Link } from 'react-router-dom';
import { useUser } from './UserContext';
import WelcomeCard from './WelcomeCard';

const AcceptableUse = () => {
  const { user } = useUser();

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      position: 'relative',
      flex: 1,
      paddingTop: '50px',
      paddingBottom: '50px',
    }}>
      <div style={{position: 'absolute', left: '0px', top: '0px', width:'100%', opacity: '10%', pointerEvents: 'none' }}>
        <img src='/mascot.png' alt=''/>
      </div>

      {!user && (
        <WelcomeCard />
      )}

      <Paper
        elevation={6}
        sx={{
          padding: '40px',
          maxWidth: '800px',
          width: '100%',
          marginBottom: '50px',
          backgroundColor: (theme) => theme.palette.background.paper,
          color: (theme) => theme.palette.text.primary,
        }}
      >
        <Typography variant="h3" gutterBottom sx={{ textAlign: 'center', marginBottom: '30px' }}>
          Acceptable Use Policy
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Purpose and Scope
        </Typography>
        <Typography variant="body1" paragraph>
          Snapperish is a platform owned and operated by Five Pack Creative, Inc., a Texas corporation. Snapperish is designed to support lawful photo contests for clubs and organizations. This Acceptable Use Policy governs all content submitted to our platform and applies to all users, including club owners, contest administrators, and members.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Prohibited Content
        </Typography>
        <Typography variant="body1" paragraph>
          The following types of content are strictly prohibited on Snapperish:
        </Typography>
        <Box component="ul" sx={{ paddingLeft: '20px', marginBottom: '20px' }}>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            <strong>Nudity or sexually explicit material</strong> of any kind, including partial nudity or suggestive content
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            <strong>Violence or graphic content</strong> including depictions of harm, injury, death, or threatening behavior
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            <strong>Child sexual abuse material (CSAM)</strong> — any content that sexually exploits, depicts, or endangers minors is strictly prohibited, illegal under federal law, and will be reported to the NCMEC CyberTipline and law enforcement as required by 18 U.S.C. § 2258A
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            <strong>Other exploitative content</strong> including but not limited to:
            <Box component="ul" sx={{ paddingLeft: '20px', marginTop: '8px' }}>
              <Typography component="li" variant="body2">Non-consensual intimate images</Typography>
              <Typography component="li" variant="body2">Content that promotes illegal activities</Typography>
            </Box>
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            <strong>Hate speech or discriminatory content</strong> targeting individuals or groups based on race, religion, gender, sexual orientation, disability, or other protected characteristics
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            <strong>Copyrighted material</strong> that you do not own or have explicit permission to use
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            <strong>Illegal content</strong> of any nature under applicable local, state, or federal laws
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            <strong>Harassment or bullying</strong> content targeting specific individuals or groups
          </Typography>
          <Typography component="li" variant="body1" sx={{ marginBottom: '8px' }}>
            <strong>Spam or promotional content</strong> unrelated to the contest theme
          </Typography>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Child Safety
        </Typography>
        <Typography variant="body1" paragraph>
          Snapperish has a zero-tolerance policy for child sexual abuse material (CSAM). CSAM is illegal under federal law and its upload, distribution, or possession is a serious criminal offense. Any content we become aware of that constitutes CSAM will be immediately removed and <strong>reported to the National Center for Missing &amp; Exploited Children (NCMEC) CyberTipline</strong> as required by 18 U.S.C. § 2258A. We will cooperate fully with law enforcement investigations.
        </Typography>
        <Typography variant="body1" paragraph>
          If you encounter suspected CSAM on this platform, report it immediately:
        </Typography>
        <Box component="ul" sx={{ paddingLeft: '20px', marginBottom: '20px' }}>
          <Typography component="li" variant="body1">
            <strong>NCMEC CyberTipline:</strong>{' '}
            <a href="https://www.missingkids.org/gethelpnow/cybertipline" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>missingkids.org/cybertipline</a>{' '}
            or 1-800-843-5678
          </Typography>
          <Typography component="li" variant="body1">
            <strong>FBI:</strong>{' '}
            <a href="https://tips.fbi.gov" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>tips.fbi.gov</a>
          </Typography>
          <Typography component="li" variant="body1">
            Snapperish staff via <Link to="/contact" style={{ color: 'inherit', textDecoration: 'underline' }}>our contact form</Link>
          </Typography>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Enforcement and Consequences
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Upload Prevention:</strong> Photos are automatically scanned using Amazon Rekognition. Photos that are automatically found to violate our policy will not be saved, and the violation will be recorded.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Immediate Removal:</strong> Photos containing prohibited content but not discovered automatically will be immediately removed from the platform without prior notice or warning by Snapperish staff.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Reporting Process:</strong> All violations that bypass Amazon Rekognition will be reported to:
        </Typography>
        <Box component="ol" sx={{ paddingLeft: '20px', marginBottom: '20px' }}>
          <Typography component="li" variant="body1">Club owners and contest administrators for immediate awareness</Typography>
          <Typography component="li" variant="body1">Snapperish staff for review and potential further action</Typography>
          <Typography component="li" variant="body1">Appropriate law enforcement authorities when required by law</Typography>
        </Box>

        <Typography variant="body1" paragraph>
          <strong>Account Consequences:</strong> Users who upload prohibited content may face:
        </Typography>
        <Box component="ul" sx={{ paddingLeft: '20px', marginBottom: '20px' }}>
          <Typography component="li" variant="body1">Immediate suspension or termination of account access</Typography>
          <Typography component="li" variant="body1">Permanent ban from the Snapperish platform</Typography>
          <Typography component="li" variant="body1">Legal action when appropriate under applicable laws</Typography>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Club Responsibilities
        </Typography>
        <Typography variant="body1" paragraph>
          Club owners and administrators have specific responsibilities including:
        </Typography>
        <Box component="ul" sx={{ paddingLeft: '20px', marginBottom: '20px' }}>
          <Typography component="li" variant="body1">Communicating these guidelines clearly to all club members</Typography>
          <Typography component="li" variant="body1">Actively monitoring contest submissions for policy compliance</Typography>
          <Typography component="li" variant="body1">Taking immediate action when violations are discovered</Typography>
          <Typography component="li" variant="body1">Cooperating with Snapperish staff during investigations</Typography>
        </Box>
        <Typography variant="body1" paragraph>
          <strong>Snapperish reserves the right to remove content, suspend accounts, or take any other action necessary to enforce this policy, regardless of whether the club owner has taken action.</strong>
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Reporting Violations
        </Typography>
        <Typography variant="body1" paragraph>
          If you encounter content that violates these guidelines, please report it immediately through any of these channels:
        </Typography>
        <Box component="ul" sx={{ paddingLeft: '20px', marginBottom: '20px' }}>
          <Typography component="li" variant="body1">Contact your club owner or contest administrators directly</Typography>
          <Typography component="li" variant="body1">
            Email Snapperish support via{' '}
            <Link to="/contact" style={{ color: 'inherit', textDecoration: 'underline' }}>
              our contact form
            </Link>{' '}
            or at{' '}
            <a href="mailto:matt+snapperish+privacy@fivepack.com" style={{ color: 'inherit' }}>matt+snapperish+privacy@fivepack.com</a>
          </Typography>
          <Typography component="li" variant="body1">Use the report function within the platform (when available)</Typography>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Automated Image Processing
        </Typography>
        <Typography variant="body1" paragraph>
          Snapperish automatically checks all images for violations but cannot guarantee that all images will be categorized correctly.
          Images that are automatically determined to be a violation of this policy will be immediately rejected with an error notice to
          the user and will not be saved by our platform. The violation will be recorded and available to Snapperish staff for review.
        </Typography>
        <Typography variant="body1" paragraph>
          All uploaded photos are also automatically screened for child sexual abuse material (CSAM) using{' '}
          <a href="https://www.microsoft.com/en-us/photodna" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
            Microsoft PhotoDNA
          </a>
          , a hash-matching technology that compares a mathematical fingerprint of each image against a database of known illegal content
          maintained by the National Center for Missing &amp; Exploited Children (NCMEC). This process does not expose your photo to
          Microsoft or any third party. Any match results in immediate rejection of the upload and triggers a mandatory report to the
          NCMEC CyberTipline as required by federal law (18 U.S.C. § 2258A).
        </Typography>
        <Typography variant="body1" paragraph>
          If you believe your image was incorrectly flagged, you may:
        </Typography>
        <Box component="ul" sx={{ paddingLeft: '20px', marginBottom: '20px' }}>
          <Typography component="li" variant="body1">Contact your club owner or contest administrators directly</Typography>
          <Typography component="li" variant="body1">
            Email Snapperish support via{' '}
            <Link to="/contact" style={{ color: 'inherit', textDecoration: 'underline' }}>
              our contact form
            </Link>{' '}
            at{' '}
            <a href="mailto:matt+snapperish+privacy@fivepack.com" style={{ color: 'inherit' }}>matt+snapperish+privacy@fivepack.com</a>
          </Typography>
        </Box>
        <Typography variant="body1" paragraph>
          We aim to respond to all incorrectly flagged image appeals within 48 hours.
        </Typography>
        <Typography variant="body1" paragraph>
          When you upload a photo, Snapperish also performs the following automatic processing:
        </Typography>
        <Box component="ul" sx={{ paddingLeft: '20px', marginBottom: '20px' }}>
          <Typography component="li" variant="body1">
            <strong>Location metadata removal</strong> — GPS and other location data embedded in your photo (EXIF metadata) is
            automatically stripped before the image is stored or shared. Your device location is never exposed to other users.
          </Typography>
          <Typography component="li" variant="body1">
            <strong>Format conversion</strong> — Some photo formats (such as HEIC) are automatically converted to JPEG for
            compatibility.
          </Typography>
          <Typography component="li" variant="body1">
            <strong>Compression</strong> — Photos larger than 20 MB are automatically compressed to reduce file size. Image quality
            is preserved as much as possible during this process.
          </Typography>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Legal Compliance and Cooperation
        </Typography>
        <Typography variant="body1" paragraph>
          Snapperish cooperates fully with law enforcement agencies and will report illegal content as required by applicable laws and regulations. This includes mandatory reporting of child sexual abuse material (CSAM) to the NCMEC CyberTipline under 18 U.S.C. § 2258A. We maintain detailed logs of all reported violations and may provide this information to authorities when legally required or requested.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Updates to This Policy
        </Typography>
        <Typography variant="body1" paragraph>
          This Acceptable Use Policy may be updated periodically to reflect changes in law, platform features, or community standards. Users will be notified of significant changes through the platform or email communications.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ marginTop: '30px' }}>
          Agreement and Compliance
        </Typography>
        <Typography variant="body1" paragraph>
          By using Snapperish, you acknowledge that you have read, understood, and agree to comply with this Acceptable Use Policy. Violation of these terms may result in immediate account termination and potential legal consequences.
        </Typography>
        <Typography variant="body1" paragraph>
          Five Pack Creative, Inc., 7460 Warren Parkway, Suite 100, Frisco, TX 75034
        </Typography>

        <Box sx={{
          textAlign: 'center',
          marginTop: '40px',
          padding: '20px',
          backgroundColor: (theme) => theme.palette.action.hover,
          borderRadius: '8px'
        }}>
          <Typography variant="body2" color="text.secondary">
            Last updated: April 2026
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ marginTop: '8px' }}>
            Questions about this policy? <Link to="/contact" style={{ color: 'inherit' }}>Contact us</Link>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default AcceptableUse;